import {
    CreateCompanyUserInvitesRequest,
    GetCompanyUsersResponse
} from '@/api';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useCompaniesTranslation, useCompanyPermissions } from '../hooks';
import { addOrUpdateCompanyUsersCache } from '../queries/company-cache';

type UseInviteUsersActionData = {
    /** Whether this viewer can invite members to the company right now. */
    isAllowed: boolean;
    /** Why not, ready to show in a tooltip. Empty while allowed. */
    reason: string;
    inviteUsers: (
        data: CreateCompanyUserInvitesRequest
    ) => Promise<GetCompanyUsersResponse>;
    loading: boolean;
    error: string;
};

/**
 * Inviting members to the company: whether this viewer may do it, why not when
 * they may not, and the call itself. Kept together so a caller can wire up a
 * button — disabled state, tooltip, spinner and click — from one hook.
 */
export function useInviteUsersAction(): UseInviteUsersActionData {
    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    const { canEditMembers } = useCompanyPermissions();

    const [isAllowed, reason] = useMemo(() => {
        if (!canEditMembers) {
            return [false, t('actions.invite_users.reason_not_permitted')];
        }

        return [true, ''];
    }, [canEditMembers, t]);

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error
    // `POST /v3/users/invites` declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'USER_ALREADY_INVITED_OR_JOINED')) {
                return t(
                    'actions.invite_users.error_already_invited_or_joined'
                );
            }
            if (isApiError(error, 'CANNOT_INVITE_USERS_WITH_OWNER')) {
                return t('actions.invite_users.error_cannot_assign_owner');
            }
            if (isApiError(error, 'NOT_SUBSCRIBED')) {
                return t('actions.invite_users.error_not_subscribed');
            }
            if (isApiError(error, 'NO_MORE_LICENSES')) {
                return t('actions.invite_users.error_no_more_licenses');
            }
            if (isApiError(error, 'TEAM_NOT_FOUND')) {
                return t('actions.invite_users.error_team_not_in_company');
            }
            // Both mean the caller is not allowed to invite; neither tells the
            // user anything they can act on beyond that.
            if (
                isApiError(error, 'NO_PERMISSION') ||
                isApiError(error, 'COMPANY_USER_NOT_FOUND')
            ) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'COMPANY_NOT_FOUND')) {
                return t('shared.errors.company_not_found');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: (data: CreateCompanyUserInvitesRequest) =>
            openApiRef.current.users
                .inviteUsers(data)
                .then((response) => response.data),
        onSuccess: (data) => {
            data.data.users.forEach((user) => {
                addOrUpdateCompanyUsersCache(queryClient, user);
            });

            UIToast.success(t('actions.invite_users.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        isAllowed,
        reason,
        inviteUsers: (data) => mutation.mutateAsync(data),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
